import React, { useEffect, useState } from "react";
import { supabase } from "../../supabaseClient";
import {
  listDropboxFolders,
  type DropboxFolderList,
} from "../../shared/dropboxClient";

/* ============================================================
   DropboxFolderPicker
   ----------------------------------------------------------------
   Full-screen sheet for choosing where Save to Dropbox puts files:
   tap a folder to open it, "Back" / the path crumbs to go up, then
   "Save to this folder". Two switches decide the rest:
   - a folder for each job ("<Town> <Last>") inside it — on by default;
   - move to next year's folder each January — offered only when the
     open folder's path contains this year, on by default.
   The server turns the choice into the stored folder template.

   mode="open" reuses the same browser for Open from Dropbox: estimate
   files (.est / legacy .DUest, newest first) are listed under the
   folders; tapping one calls onOpenFile. No folder switches; a link at
   the bottom falls back to the phone's own file picker.
   ============================================================ */

type Props = {
  /** Folder to open first, e.g. the current setting's folder; "/" = top. */
  startPath?: string;
  busy?: boolean;
  onCancel: () => void;
  mode?: "folder" | "open";
  /** mode="folder": the chosen folder + switches. */
  onPick?: (pick: { pickedPath: string; perJobFolder: boolean; rollYear: boolean }) => void;
  /** mode="open": a tapped estimate file. */
  onOpenFile?: (path: string) => void;
  /** mode="open": use the phone's own file picker instead. */
  onPhoneFiles?: () => void;
  /** mode="open": shown under the list (e.g. an open failure). */
  message?: string | null;
};

const shortDate = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function DropboxFolderPicker({
  startPath = "/",
  busy = false,
  onCancel,
  mode = "folder",
  onPick,
  onOpenFile,
  onPhoneFiles,
  message = null,
}: Props) {
  const openMode = mode === "open";
  const [path, setPath] = useState(startPath || "/");
  const [list, setList] = useState<DropboxFolderList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [perJobFolder, setPerJobFolder] = useState(true);
  const [rollYear, setRollYear] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void listDropboxFolders(supabase, path, { includeFiles: openMode }).then((r) => {
      if (cancelled) return;
      setLoading(false);
      if (r.ok) {
        setList(r.data);
      } else if (path !== "/" && /no longer exists/.test(r.error)) {
        // The remembered folder was moved or deleted — start from the top.
        setPath("/");
      } else {
        setError(r.error);
      }
    });
    return () => {
      cancelled = true;
    };
    // openMode is fixed for the sheet's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const segments = path === "/" ? [] : path.split("/").filter(Boolean);
  const goTo = (depth: number) =>
    setPath(depth === 0 ? "/" : "/" + segments.slice(0, depth).join("/"));
  const hasYear = !!list?.hasYear && list.path === path;
  const year = new Date().getFullYear();
  const preview =
    [...segments, ...(perJobFolder ? ["Woodbury Sabo"] : []), "Woodbury_Sabo.est"].join(" › ");
  const canSave = !busy && !loading && !error && (segments.length > 0 || perJobFolder);

  return (
    <div
      className="m-picker"
      role="dialog"
      aria-modal="true"
      aria-label={openMode ? "Open an estimate from Dropbox" : "Choose a Dropbox folder"}
    >
      <div className="m-picker-head">
        <button type="button" className="m-picker-headbtn" onClick={onCancel}>
          Cancel
        </button>
        <span className="m-picker-title">{openMode ? "Open from Dropbox" : "Choose a folder"}</span>
        <span className="m-picker-headbtn" aria-hidden="true" />
      </div>

      <nav className="m-picker-crumbs" aria-label="Folder path">
        <button type="button" className="m-picker-crumb" onClick={() => goTo(0)} disabled={!segments.length}>
          Dropbox
        </button>
        {segments.map((seg, i) => (
          <React.Fragment key={i}>
            <span className="m-picker-sep">›</span>
            <button
              type="button"
              className="m-picker-crumb"
              onClick={() => goTo(i + 1)}
              disabled={i === segments.length - 1}
            >
              {seg}
            </button>
          </React.Fragment>
        ))}
      </nav>

      <div className="m-picker-list">
        {segments.length ? (
          <button type="button" className="m-picker-row m-picker-row--back" onClick={() => goTo(segments.length - 1)}>
            ‹ Back
          </button>
        ) : null}
        {loading ? (
          <div className="m-picker-note">Loading folders…</div>
        ) : error ? (
          <div className="m-auth-msg m-auth-msg--err" role="alert">{error}</div>
        ) : !list?.folders.length && !(openMode && list?.files?.length) ? (
          <div className="m-picker-note">
            {openMode ? "No folders or estimates here." : "No folders inside. You can save here."}
          </div>
        ) : (
          <>
            {openMode
              ? (list?.files || []).map((f) => (
                  <button
                    key={f.path}
                    type="button"
                    className="m-picker-row m-picker-row--file"
                    disabled={busy}
                    onClick={() => onOpenFile?.(f.path)}
                  >
                    <span className="m-picker-icon" aria-hidden="true">📄</span>
                    <span className="m-picker-name">
                      {f.name}
                      {f.modified ? <span className="m-picker-meta">{shortDate(f.modified)}</span> : null}
                    </span>
                  </button>
                ))
              : null}
            {(list?.folders || []).map((f) => (
              <button key={f.path} type="button" className="m-picker-row" onClick={() => setPath(f.path)}>
                <span className="m-picker-icon" aria-hidden="true">📁</span>
                <span className="m-picker-name">{f.name}</span>
                <span className="m-picker-chev" aria-hidden="true">›</span>
              </button>
            ))}
          </>
        )}
      </div>

      {openMode ? (
        <div className="m-picker-foot">
          {busy ? <div className="m-picker-preview">Opening…</div> : null}
          {message ? (
            <div className="m-auth-msg m-auth-msg--err" role="alert">{message}</div>
          ) : null}
          {onPhoneFiles ? (
            <button type="button" className="m-action-link" onClick={onPhoneFiles}>
              Open from phone files instead
            </button>
          ) : null}
        </div>
      ) : (

      <div className="m-picker-foot">
        <label className="m-picker-switch">
          <input type="checkbox" checked={perJobFolder} onChange={(e) => setPerJobFolder(e.target.checked)} />
          <span>Make a folder for each job (Town Last)</span>
        </label>
        {hasYear ? (
          <label className="m-picker-switch">
            <input type="checkbox" checked={rollYear} onChange={(e) => setRollYear(e.target.checked)} />
            <span>Switch to the {year + 1} folder next January</span>
          </label>
        ) : null}
        <div className="m-picker-preview">Saves as: {preview}</div>
        <button
          type="button"
          className="m-settings-logout m-settings-primary"
          disabled={!canSave}
          onClick={() => onPick?.({ pickedPath: path, perJobFolder, rollYear: hasYear && rollYear })}
        >
          {busy ? "Saving…" : "Save to this folder"}
        </button>
      </div>
      )}
    </div>
  );
}
